import { useFocusEffect } from '@react-navigation/native';
import { getCurrentTutorial, isInsideOnboardingAtom, isLoggedInAtom } from 'app/atoms';
import { useTutorials } from 'app/components/tutorial-overlay';
import {
  isBlockingModalVisible as isBlockingModalVisibleNow,
  useIsBlockingModalVisible,
} from 'app/hooks/use-is-blocking-modal-visible';
import { useOnePreference } from 'app/network/preferences';
import { useUser } from 'app/network/user';
import { TrialModalType } from 'app/types/preferences';
import { Tutorial } from 'app/types/tutorial';
import { useAtomValue } from 'jotai';
import { useRef } from 'react';

export function useHomeExploreTutorial(enabled = true) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const isInsideOnboarding = useAtomValue(isInsideOnboardingAtom);
  const { data: user } = useUser();
  const { startTutorial } = useTutorials();
  const tutorialsCompleted = useOnePreference('tutorialsCompleted').data?.value;
  const isBlockingModalVisible = useIsBlockingModalVisible();
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const trialModalPref = useOnePreference('trialModal').data?.value;
  const introFlowPending = trialModalPref !== TrialModalType.never;

  useFocusEffect(() => {
    const canShowOnHome = Boolean(user?.acceptedGuidelineDate);
    if (!enabled || !isLoggedIn || isInsideOnboarding || !canShowOnHome || introFlowPending) {
      return;
    }

    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }

    if (isBlockingModalVisible) return;
    if (getCurrentTutorial()) return;
    if (tutorialsCompleted?.includes(Tutorial.HomeExplore)) return;

    // Wait briefly to ensure startup modal transitions are fully complete.
    settleTimerRef.current = setTimeout(() => {
      settleTimerRef.current = null;
      if (isBlockingModalVisibleNow()) return;
      if (getCurrentTutorial()) return;
      startTutorial(Tutorial.HomeExplore);
    }, 450);

    return () => {
      if (settleTimerRef.current) {
        clearTimeout(settleTimerRef.current);
        settleTimerRef.current = null;
      }
    };
  });
}
